import { useState } from 'react'
import type { AuditEntry } from '@lms/shared-types'
import { AiAssistedChip } from '../AiAssistedChip'
import { ChangeLine, visibleChanges } from './ChangeDiff'
import { formatTimestamp, relativeTime } from './audit-format'

/**
 * One node in a per-object change timeline (CRM-activity-feed style):
 * a dot on the connector line, the event label, "actor · relative time", and
 * an inline "show change / hide change" toggle that reveals the field diffs as
 * ~~before~~ → **after**. The connector line itself is drawn by the parent.
 */
export function AuditTimelineEvent({ entry }: { entry: AuditEntry }): JSX.Element {
  const [open, setOpen] = useState(false)
  const changes = visibleChanges(entry.changeSummary)
  const hasChanges = changes.length > 0

  const aiProvenance = entry.aiProvenance
  const isAiAssisted = aiProvenance != null && aiProvenance.aiInvocationId != null

  return (
    <li
      data-testid="audit-event-line"
      style={{ position: 'relative', padding: '0 0 18px 26px', listStyle: 'none' }}
    >
      {/* node dot, centred on the parent's connector line (left: 6px) */}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          left: 2,
          top: 4,
          width: 9,
          height: 9,
          borderRadius: '50%',
          background: 'var(--text-3)',
          boxShadow: '0 0 0 3px var(--surface)',
        }}
      />
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span
          data-testid="audit-event-label"
          style={{ fontWeight: 600, color: 'var(--text)', fontSize: 13 }}
          title={entry.action}
        >
          {entry.label || entry.action}
        </span>
        {isAiAssisted ? (
          <AiAssistedChip
            modelId={aiProvenance.aiModelId ?? ''}
            promptVersion={aiProvenance.aiPromptVersion ?? ''}
            proseOrigin={null}
          />
        ) : null}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 1 }}>
        <span>{entry.actorName ?? 'System'}</span>
        {entry.actorRole ? <span> ({entry.actorRole})</span> : null}
        <span> · </span>
        <span title={formatTimestamp(entry.createdAt)}>{relativeTime(entry.createdAt)}</span>
      </div>

      {hasChanges ? (
        <>
          <button
            type="button"
            aria-expanded={open}
            data-testid="show-change-toggle"
            onClick={() => setOpen((v) => !v)}
            style={{
              marginTop: 4,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 11,
              fontFamily: 'inherit',
              color: 'var(--primary-fg)',
              background: 'none',
              border: 'none',
              padding: 0,
              minHeight: 20,
              cursor: 'pointer',
            }}
          >
            <span style={{ fontSize: 14, lineHeight: 1 }}>{open ? '▾' : '▸'}</span>
            <span style={{ textDecoration: 'underline', textUnderlineOffset: 2 }}>
              {open ? 'hide change' : 'show change'}
              {!open && changes.length > 1 ? ` (${changes.length})` : ''}
            </span>
          </button>
          {open ? (
            <div
              data-testid="show-change-body"
              style={{
                marginTop: 6,
                paddingLeft: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {changes.map((c) => (
                <ChangeLine key={c.field} field={c.field} before={c.before} after={c.after} />
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </li>
  )
}
